import { CodeEditorLanguages } from '@brandfolder/react';
import { Fragment, FunctionComponent, useContext, useState } from 'react';

import { TinyMceInline } from '@components/wysiwyg';
import { defaultHtml } from '@constants/block';
import { BlockContext } from '@context/blocks';
import { DragAndDropContext } from '@context/drag-and-drop';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';
import { PublishContext } from '@context/publish';
import { BlockTypes } from '@enums/blocks';
import { Block } from '@typings/block';
import {
  getBlockDataIsPdf,
  getBlockDataIsColor,
  getBlockDataIsHtml,
  getBlockDataIsImage,
  updateBlock
} from '@utilities/block';

import { CodeEditor } from './code/code-editor';
import { ColorRender } from './color/color-render';
import { BlockEditor } from './editor/block-editor';
import { HtmlRender } from './html/html-render';
import { ImageRender } from './image/image-render';
import { PdfRender } from './pdf/pdf-render';
import { SectionRender } from './section/section-render';

interface BlockRenderProps {
  block: Block;
  index: number;
  first?: boolean;
  last?: boolean;
  only?: boolean;
  section?: boolean;
}

export const BlockRender: FunctionComponent<BlockRenderProps> = (props) => {
  const { block, first, index, last, only, section } = props;
  const { data, key, parentKey, type } = block;

  const { blocks, setBlocks } = useContext(BlockContext);
  const { dragging } = useContext(DragAndDropContext);
  const { editable } = useContext(PermissionLevelContext);
  const { preview } = useContext(PreviewContext);
  const { setPublish } = useContext(PublishContext);

  const html = type === BlockTypes.Html;

  const getDefaultEdit = (): boolean => {
    if (!html) return false;
    if (getBlockDataIsHtml(data) && data.html === defaultHtml) return true;
    return false;
  };

  const [edit, setEdit] = useState(getDefaultEdit());

  const editing = editable && !preview;

  if (!section && parentKey) return null;

  return (
    // must be fragment so we don't render superfluous HTML elements
    // so, DO NOT replace `<Fragment>` with `<div>`, `<section>`, etc
    <Fragment>
      {editing ? (
        <>
          {type === BlockTypes.Pdf && getBlockDataIsPdf(data) ? (
            <BlockEditor block={block} first={first} index={index} last={last} only={only}>
              <PdfRender block={block} />
            </BlockEditor>
          ) : type === BlockTypes.Color && getBlockDataIsColor(data) ? (
            <BlockEditor block={block} first={first} index={index} last={last} only={only}>
              <ColorRender block={block} id={key} />
            </BlockEditor>
          ) : type === BlockTypes.Text && getBlockDataIsHtml(data) ? (
            <BlockEditor block={block} first={first} index={index} last={last} only={only}>
              <TinyMceInline
                disabled={dragging}
                block={block}
                onBlur={(content) => {
                  if (data.html !== content) {
                    setBlocks([...updateBlock({ ...block, data: { html: content } }, blocks)]);
                    setPublish(true);
                  }
                }}
              />
            </BlockEditor>
          ) : type === BlockTypes.Html && getBlockDataIsHtml(data) ? (
            <>
              <BlockEditor
                block={block}
                edit={edit}
                first={first}
                index={index}
                last={last}
                only={only}
                setEdit={setEdit}
              >
                {edit ? (
                  <CodeEditor block={block} label="HTML" language={CodeEditorLanguages.Html} />
                ) : (
                  <HtmlRender block={block} />
                )}
              </BlockEditor>
            </>
          ) : !parentKey && type === BlockTypes.Section ? (
            <BlockEditor block={block} first={first} index={index} last={last} only={only}>
              <SectionRender block={block} />
            </BlockEditor>
          ) : type === BlockTypes.Image && getBlockDataIsImage(data) ? (
            <BlockEditor block={block} first={first} index={index} last={last} only={only}>
              <ImageRender block={block} />
            </BlockEditor>
          ) : null}
        </>
      ) : type === BlockTypes.Pdf && getBlockDataIsPdf(data) ? (
        <PdfRender block={block} />
      ) : type === BlockTypes.Color && getBlockDataIsColor(data) ? (
        <ColorRender block={block} id={key} />
      ) : (type === BlockTypes.Text || type === BlockTypes.Html) && getBlockDataIsHtml(data) ? (
        <HtmlRender block={block} />
      ) : type === BlockTypes.Section ? (
        <SectionRender block={block} />
      ) : type === BlockTypes.Image && getBlockDataIsImage(data) ? (
        <ImageRender block={block} />
      ) : null}
    </Fragment>
  );
};
